import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useCallback } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  const { t } = useTranslation();

  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');
    setFormStatus('idle');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotValue = (formData.get('phone_alt') as string || '').trim();

    if (honeypotValue) {
      setFormStatus('success');
      setSubmitting(false);
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9q2tivnahb7382ov5ag', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch (_err) {
        parsed = {};
      }

      const code = parsed?.code;
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else if (!response.ok || !code || code !== 'OK' || (typeof serverMsg === 'string' && (serverMsg.includes('spam') || serverMsg.includes('form data is spam')))) {
        setFormStatus('error');
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
      } else {
        setFormStatus('error');
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
      }
    } catch (_err) {
      setFormStatus('error');
      setFormError('网络错误，请稍后重试或直接发送邮件联系我们。');
    } finally {
      setSubmitting(false);
    }
  }, []);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `${t('about.title')} - ${t('siteName')}`,
    description: t('about.subtitle'),
    url: `${window.location.origin}/about`,
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: t('siteName'),
    url: window.location.origin,
    description: t('siteDescription'),
    email: t('footer.email'),
    address: {
      '@type': 'PostalAddress',
      addressLocality: '深圳',
      addressRegion: '广东',
      addressCountry: 'CN',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      email: t('footer.email'),
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          {/* Page Header */}
          <section className="bg-background-100 border-b border-background-200">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
              <Breadcrumb items={[{ label: t('about.title') }]} />
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mt-4 font-heading">
                {t('about.title')}
              </h1>
              <p className="text-foreground-500 mt-3 text-lg">
                {t('about.subtitle')}
              </p>
            </div>
          </section>

          {/* Brand Introduction */}
          <section className="py-14 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex flex-col lg:flex-row gap-12 items-center">
                <div className="lg:w-1/2">
                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-50 text-primary-600 text-xs font-medium mb-4">
                    <i className="ri-building-line"></i>
                    {t('about.brandIntro')}
                  </span>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6 font-heading">
                    专注拔插知识，守护每一次连接
                  </h2>
                  <div className="space-y-4 text-foreground-600 leading-relaxed">
                    <p>{t('about.brandDesc1')}</p>
                    <p>{t('about.brandDesc2')}</p>
                    <p>{t('about.brandDesc3')}</p>
                  </div>
                </div>
                <div className="lg:w-1/2">
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20workspace%20with%20laptop%20and%20organized%20electronics%20testing%20equipment%20on%20clean%20white%20desk%2C%20various%20USB%20cables%20and%20connectors%20neatly%20arranged%2C%20warm%20natural%20lighting%2C%20cream%20and%20beige%20interior%2C%20professional%20tech%20blog%20aesthetic%2C%20soft%20focus&width=1200&height=800&seq=about-brand-15&orientation=landscape"
                      alt="拔插指南工作环境"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Reputation / Testimonials */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                  <i className="ri-star-line"></i>
                  {t('about.reputation')}
                </span>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                  来自用户的声音
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    name: '王工',
                    role: '硬件工程师',
                    text: '作为从业十年的硬件工程师，我本以为自己对拔插已经足够了解。看了拔插指南的几篇文章后，才发现自己有很多操作习惯需要纠正。特别是USB接口的正确握持方式，真的醍醐灌顶。',
                    rating: 5,
                  },
                  {
                    name: '李老师',
                    role: '中学信息技术教师',
                    text: '拔插指南的内容非常实用，我已经把其中的安全拔插原则融入到了课堂教学中。学生们对"握插头不拉线"这个简单的道理记得特别牢。感谢平台提供了这么专业的内容！',
                    rating: 5,
                  },
                  {
                    name: '张同学',
                    role: '大学生',
                    text: '我的笔记本Type-C接口之前老是接触不良，看了贵站关于接口清洁和维护的文章后，用压缩空气清理了一下，现在完全恢复正常了！省了一笔维修费，太感谢了。',
                    rating: 5,
                  },
                  {
                    name: '赵女士',
                    role: '办公室文员',
                    text: '以前从来不知道接口还需要定期清洁维护，以为是坏了就要换。跟着指南清洁了一次办公室所有设备的接口，效果立竿见影。特别推荐给所有办公室同事！',
                    rating: 5,
                  },
                  {
                    name: '陈师傅',
                    role: '电子维修师',
                    text: '拔插指南的内容准确度很高，和我多年的维修经验完全吻合。特别推荐"拔插误区"系列，很多客户的问题都是这些错误操作导致的，大家都应该看看。',
                    rating: 5,
                  },
                  {
                    name: '刘经理',
                    role: 'IT部门主管',
                    text: '公司设备接口故障率一直偏高，我把拔插指南的文章作为培训资料发给团队学习后，接口报修率下降了40%以上。效果非常显著，强烈推荐给企业IT部门。',
                    rating: 5,
                  },
                ].map((testimonial, i) => (
                  <div key={i} className="bg-background-50 rounded-lg p-6 border border-background-200/70">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: testimonial.rating }).map((_, j) => (
                        <i key={j} className="ri-star-fill text-accent-500 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      &ldquo;{testimonial.text}&rdquo;
                    </p>
                    <div className="flex items-center gap-3 pt-4 border-t border-background-200">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-bold text-xs">
                        {testimonial.name.charAt(0)}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-foreground-900">{testimonial.name}</div>
                        <div className="text-xs text-foreground-400">{testimonial.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section className="py-14 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex flex-col lg:flex-row gap-12">
                {/* Contact Info */}
                <div className="lg:w-2/5">
                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-50 text-primary-600 text-xs font-medium mb-4">
                    <i className="ri-mail-line"></i>
                    {t('about.contactTitle')}
                  </span>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4 font-heading">
                    有任何问题？随时联系我们
                  </h2>
                  <p className="text-foreground-500 leading-relaxed mb-8">
                    {t('about.contactDesc')}
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                        <i className="ri-mail-line text-lg"></i>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-foreground-900">邮箱</div>
                        <a href="mailto:contact@bachabacha.com" className="text-sm text-foreground-500 hover:text-primary-500 transition-colors">
                          contact@bachabacha.com
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                        <i className="ri-map-pin-line text-lg"></i>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-foreground-900">{t('about.address')}</div>
                        <div className="text-sm text-foreground-500">{t('about.addressValue')}</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                        <i className="ri-time-line text-lg"></i>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-foreground-900">工作时间</div>
                        <div className="text-sm text-foreground-500">周一至周五 9:00 - 18:00</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Form */}
                <div className="lg:w-3/5">
                  <div className="bg-background-100 rounded-lg p-6 md:p-8 border border-background-200/70">
                    <h3 className="text-lg font-bold text-foreground-950 mb-6 font-heading">
                      {t('about.contactForm')}
                    </h3>
                    <form
                      onSubmit={handleSubmit}
                      data-readdy-form="true"
                      className="space-y-5"
                    >
                      {/* Honeypot */}
                      <input
                        type="text"
                        name="phone_alt"
                        className="bachabacha-hp-field"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />

                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          {t('about.name')} <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          id="contact-name"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                          placeholder="请输入您的姓名"
                        />
                      </div>

                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          {t('about.email')} <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="email"
                          id="contact-email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                          placeholder="请输入您的邮箱"
                        />
                      </div>

                      <div>
                        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          {t('about.message')} <span className="text-red-400">*</span>
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={5}
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200 resize-none"
                          placeholder="请输入您的留言内容（500字以内）"
                        ></textarea>
                      </div>

                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full py-3 bg-primary-500 text-background-50 rounded-lg font-semibold text-sm hover:bg-primary-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        {submitting ? (
                          <span className="flex items-center justify-center gap-2">
                            <i className="ri-loader-4-line animate-spin"></i>
                            提交中...
                          </span>
                        ) : (
                          t('about.submit')
                        )}
                      </button>

                      {formStatus === 'success' && (
                        <div className="p-4 rounded-lg bg-accent-50 text-accent-700 text-sm flex items-center gap-2">
                          <i className="ri-checkbox-circle-line"></i>
                          {t('about.submitSuccess')}
                        </div>
                      )}

                      {formStatus === 'error' && (
                        <div className="p-4 rounded-lg bg-red-50 text-red-600 text-sm flex items-center gap-2">
                          <i className="ri-error-warning-line"></i>
                          {formError || t('about.submitError')}
                        </div>
                      )}
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}